import React, { useMemo } from 'react'

import { Switch, SwitchLabel, Tooltip, Typography } from '@barracuda-internal/bds-core'
import { Info } from '@barracuda-internal/bds-core/dist/Icons/Feedback'
import { useFormatMessage } from 'lib/localization'
import { useSenderIpLogic } from 'components/pages/outboundSettings/senderIp/useSenderIpLogic'
import OutboundSenderIp from 'components/pages/support/help/outboundSenderIp/OutboundSenderIp'
import { GROUP_TITLE, TOPICS } from 'components/pages/support/config'
import { PageContainer, PageTitle, FormSpacer } from 'components/libs/layout/pages/pageLayout'
import SenderIpTable from 'components/pages/outboundSettings/senderIp/SenderIpTable'
import NoDomainPolicyAccessAlert from 'components/libs/layout/pages/NoDomainPolicyAccessAlert'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import outboundSettingsStyles from 'components/pages/outboundSettings/outboundSettingsStyles'

const BASE_I18N_KEY = 'ess.outbound_settings.sender_ip'

const SenderIp: React.FC = () => {
  const isReadonly = useIsReadonly()
  const classes = outboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const [state, eventHandlers] = useSenderIpLogic()

  return useMemo(
    () => (
      <PageContainer
        isLoading={state.isLoading}
        isFailedToLoad={state.isFailed}
        dirtyCheck
        isDirtyForm={state.isDirtyForm}
      >
        <PageTitle
          title={formatMessage('title')}
          onOpenSupport={eventHandlers.helpConfig.onHelpClick}
          onCancel={eventHandlers.onCancel}
          onSubmit={eventHandlers.onSave}
          submitInProgress={state.outboundRelay.isLoading}
          isDisabledSubmit={state.isDisabledSubmit}
          help={{
            content: OutboundSenderIp,
            onClose: eventHandlers.helpConfig.onCloseHelp,
            title: TOPICS.senderIpAddressRanges,
            group: GROUP_TITLE.outboundSettings,
            isHelpOpen: eventHandlers.helpConfig.isOpen
          }}
        />
        {state.hasNoDomainPolicyAccess && <NoDomainPolicyAccessAlert />}
        {!state.hasNoDomainPolicyAccess && (
          <>
            <FormSpacer />
            <SenderIpTable delegateIsDirtyForm={eventHandlers.setIsTableDirty} />
            {!state.outboundRelay.isHidden && (
              <div className={classes.outboundRelaySection}>
                <SwitchLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      checked={state.outboundRelay.isEnabled}
                      onChange={eventHandlers.onOutboundRelayToggle}
                      color="primary"
                      data-testid="outbound-relay-toggle"
                      disabled={isReadonly || state.outboundRelay.isLoading}
                    />
                  }
                  label={
                    <div className={classes.outboundRelayLabelWrapper}>
                      <div className={classes.outboundRelayTitleRow}>
                        <Typography variant="body1">{formatMessage('outbound_relay.title')}</Typography>
                        <Tooltip
                          title={<Typography variant="body1">{formatMessage('outbound_relay.tooltip')}</Typography>}
                          placement="top"
                          classes={{ tooltip: classes.outboundRelayTooltip }}
                        >
                          <span
                            className={classes.outboundRelayTooltipIcon}
                            data-testid="outbound-relay-tooltip"
                            tabIndex={0}
                            role="button"
                            aria-label={formatMessage('outbound_relay.title')}
                          >
                            <Info fontSize="inherit" />
                          </span>
                        </Tooltip>
                      </div>
                      <Typography variant="body2" color="textSecondary">
                        {formatMessage('outbound_relay.helper_text')}
                      </Typography>
                    </div>
                  }
                />
              </div>
            )}
          </>
        )}
      </PageContainer>
    ),
    [formatMessage, state, eventHandlers, isReadonly, classes]
  )
}

export default SenderIp
